import React, { useState, useEffect, useRef, useCallback } from 'react';
import './RestartMigrationModal.css';
import { RestartMigrationModalProps } from '../types';
import { migrationApi } from '../services/migrationApi';

type StepStatus = 'idle' | 'loading' | 'success' | 'error';

interface StepState {
  status: StepStatus;
  error?: string;
}

const RestartMigrationModal: React.FC<RestartMigrationModalProps> = ({ isOpen, onClose, migrationData }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [resultUrl, setResultUrl] = useState<string>('');
  const [migrationResponse, setMigrationResponse] = useState<any>(null);
  const [isMigrationCompleted, setIsMigrationCompleted] = useState<boolean>(false);
  const [migrationId] = useState<string>(migrationData._id);
  const [steps, setSteps] = useState<{
    step1: StepState;
    step2: StepState;
    step3: StepState;
  }>({
    step1: { status: 'idle' },
    step2: { status: 'idle' },
    step3: { status: 'idle' }
  });
  const [repoName, setRepoName] = useState<string>('');
  const [branchName, setBranchName] = useState<string>('');
  const [source] = useState<string>(migrationData.source || 'endevor');
  const [destination] = useState<string>(migrationData.destination || '');
  const abortControllerRef = useRef<AbortController | null>(null);
  const [hasStarted, setHasStarted] = useState<boolean>(false);

  const executeMigrationProcessFromStep = useCallback(async (startFromStep: number) => {
    setIsSubmitting(true);
    let currentExecutingStep = -1;
    let activeMigrationId = migrationId;
    
    try {
      // Step 1: Load
      if (startFromStep <= 1) {
        console.log('Starting Step 1: Load');
        currentExecutingStep = 1;
        updateStepStatus('step1', 'loading');
        setCurrentStep(1);
        
        await migrationApi.retryLoadDb(migrationId);
        updateStepStatus('step1', 'success');
        console.log('Step 1 completed: Load');
      }

      // Step 2: Transform
      if (startFromStep <= 2) {
        console.log('Starting Step 2: Transform');
        currentExecutingStep = 2;
        updateStepStatus('step2', 'loading');
        setCurrentStep(2);
        
        await migrationApi.transform(source, destination, activeMigrationId, repoName, branchName);
        updateStepStatus('step2', 'success');
        console.log('Step 2 completed: Transform');
      }
      
      // Step 3: Migrate
      if (startFromStep <= 3) {
        console.log('Starting Step 3: Migrate');
        currentExecutingStep = 3;
        updateStepStatus('step3', 'loading');
        setCurrentStep(3);
        
        const response = await migrationApi.migrate(source, destination, activeMigrationId, repoName, branchName);
        updateStepStatus('step3', 'success');
        setMigrationResponse(response);
        setIsMigrationCompleted(true);
        
        try {
          await migrationApi.cleanup();
          console.log('Cleanup completed automatically after migration');
        } catch (cleanupError) {
          console.error('Automatic cleanup failed:', cleanupError);
        }
        
        // Handle response URL
        if (response.data && typeof response.data === 'string') {
          setResultUrl(response.data);
        } else if (response.data && typeof response.data === 'object' && 'downloadUrl' in response.data) {
          const s3Data = response.data as any;
          setResultUrl(s3Data.downloadUrl || '');
        } else if (response.data && typeof response.data === 'object' && 'data' in response.data) {
          const s3Response = response.data as any;
          if (s3Response.data && s3Response.data.downloadUrl) {
            setResultUrl(s3Response.data.downloadUrl);
          }
        }
        console.log('Step 3 completed: Migrate', response);
      }
      
    } catch (error: any) {
      console.error('Migration process failed at step:', currentExecutingStep, error);
      const errorMessage = extractErrorMessage(error);
      
      try {
        await migrationApi.cleanup();
        console.log('Cleanup completed automatically after migration');
      } catch (cleanupError) {
        console.error('Automatic cleanup failed:', cleanupError);
      }
      
      // Determine which step failed
      if (currentExecutingStep === 1) {
        updateStepStatus('step1', 'error', errorMessage);
      } else if (currentExecutingStep === 2) {
        updateStepStatus('step2', 'error', errorMessage);
      } else if (currentExecutingStep === 3) {
        updateStepStatus('step3', 'error', errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [migrationId, repoName, branchName, source, destination]);

  const handleStartMigration = (): void => {
    if (!repoName.trim() || !branchName.trim()) return;
    setHasStarted(true);
    executeMigrationProcessFromStep(1);
  };

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasStarted(false);
      setCurrentStep(0);
      setIsSubmitting(false);
      setResultUrl('');
      setMigrationResponse(null);
      setIsMigrationCompleted(false);
      setRepoName('');
      setBranchName('');
      setSteps({
        step1: { status: 'idle' },
        step2: { status: 'idle' },
        step3: { status: 'idle' }
      });
    }
  }, [isOpen]);

  const updateStepStatus = (step: 'step1' | 'step2' | 'step3', status: StepStatus, error?: string) => {
    setSteps(prev => ({
      ...prev,
      [step]: { status, error }
    }));
  };

  // Helper function to extract error message from API response
  const extractErrorMessage = (error: any): string => {
    if (error?.response?.data?.detail) {
      return error.response.data.detail;
    }
    if (error?.response?.data?.message) {
      return error.response.data.message;
    }
    if (error?.response?.data?.error) {
      return error.response.data.error;
    }
    if (error?.message) {
      return error.message;
    }
    return 'An unexpected error occurred';
  };

  const retryStep = async (stepNumber: number) => {
    setIsMigrationCompleted(false);
    
    if (stepNumber === 1) {
      setSteps(prev => ({
        step1: { status: 'idle' },
        step2: { status: 'idle' },
        step3: { status: 'idle' }
      }));
      await executeMigrationProcessFromStep(1);
    } else if (stepNumber === 2) {
      setSteps(prev => ({
        ...prev,
        step2: { status: 'idle' },
        step3: { status: 'idle' }
      }));
      await executeMigrationProcessFromStep(2);
    } else if (stepNumber === 3) {
      setSteps(prev => ({
        ...prev,
        step3: { status: 'idle' }
      }));
      await executeMigrationProcessFromStep(3);
    }
  };

  // Check if migration is currently ongoing
  const isMigrationOngoing = (): boolean => {
    return isSubmitting || 
           steps.step1.status === 'loading' || 
           steps.step2.status === 'loading' || 
           steps.step3.status === 'loading';
  };

  const handleClose = (): void => {
    // Prevent closing during ongoing migration
    if (isMigrationOngoing()) {
      return;
    }
    
    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Store completion status before resetting
    const wasCompleted = isMigrationCompleted;
    
    // Reset all state
    setCurrentStep(0);
    setIsSubmitting(false);
    setResultUrl('');
    setMigrationResponse(null);
    setIsMigrationCompleted(false);
    setRepoName('');
    setBranchName('');
    setSteps({
      step1: { status: 'idle' },
      step2: { status: 'idle' },
      step3: { status: 'idle' }
    });
    
    // Call onClose with completion status
    onClose(wasCompleted);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container restart-modal-container">
        {/* Modal Header */}
        <div className="modal-header">
          <h2 className="modal-title">Restart Migration</h2>
          <button 
            className="close-button" 
            onClick={handleClose}
            disabled={isMigrationOngoing()}
            title={isMigrationOngoing() ? 'Cannot close during migration' : 'Close'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        {/* Source and Destination Display */}
        <div className="restart-migration-info">
          <div className="info-row">
            <span className="info-label">Source:</span>
            <span className="info-value">{source}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Destination:</span>
            <span className="info-value">{destination}</span>
          </div>
        </div>

        {/* Repo name and Branch name (required before starting) */}
        {!hasStarted && !isMigrationCompleted && (
          <div className="restart-migration-form">
            <div className="form-group">
              <label htmlFor="restart-repo_name" className="form-label">
                Repo Name<span className="required">*</span>
              </label>
              <input
                id="restart-repo_name"
                type="text"
                value={repoName}
                onChange={(e) => setRepoName(e.target.value)}
                className="form-input"
                placeholder="Enter repository name"
                disabled={isSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="restart-branch_name" className="form-label">
                Branch Name<span className="required">*</span>
              </label>
              <input
                id="restart-branch_name"
                type="text"
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                className="form-input"
                placeholder="Enter branch name"
                disabled={isSubmitting}
              />
            </div>
            <button
              type="button"
              className="migration-submit-button"
              onClick={handleStartMigration}
              disabled={isSubmitting || !repoName.trim() || !branchName.trim()}
            >
              Start Migration
            </button>
          </div>
        )}

        {/* Progress Steps */}
        <div className="progress-container">
          <div className="progress-steps">
            <div className={`step ${currentStep >= 1 ? 'active' : ''} ${steps.step1.status === 'success' ? 'completed' : ''} ${steps.step1.status === 'error' ? 'error' : ''}`}>
              <div className="step-circle">
                {steps.step1.status === 'loading' ? (
                  <div className="spinner"></div>
                ) : steps.step1.status === 'success' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : steps.step1.status === 'error' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <span className="step-number">1</span>
                )}
              </div>
              <span className="step-label">Load</span>
              {steps.step1.status === 'error' && (
                <button className="retry-button" onClick={() => retryStep(1)}>
                  Retry
                </button>
              )}
            </div>
            
            <div className={`step-connector ${steps.step1.status === 'success' ? 'active' : ''}`}></div>
            
            <div className={`step ${currentStep >= 2 ? 'active' : ''} ${steps.step2.status === 'success' ? 'completed' : ''} ${steps.step2.status === 'error' ? 'error' : ''}`}>
              <div className="step-circle">
                {steps.step2.status === 'loading' ? (
                  <div className="spinner"></div>
                ) : steps.step2.status === 'success' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : steps.step2.status === 'error' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <span className="step-number">2</span>
                )}
              </div>
              <span className="step-label">Transform</span>
              {steps.step2.status === 'error' && (
                <button className="retry-button" onClick={() => retryStep(2)}>
                  Retry
                </button>
              )}
            </div>
            
            <div className={`step-connector ${steps.step2.status === 'success' ? 'active' : ''}`}></div>
            
            <div className={`step ${currentStep >= 3 ? 'active' : ''} ${steps.step3.status === 'success' ? 'completed' : ''} ${steps.step3.status === 'error' ? 'error' : ''}`}>
              <div className="step-circle">
                {steps.step3.status === 'loading' ? (
                  <div className="spinner"></div>
                ) : steps.step3.status === 'success' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : steps.step3.status === 'error' ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  <span className="step-number">3</span>
                )}
              </div>
              <span className="step-label">Migrate</span>
              {steps.step3.status === 'error' && (
                <button className="retry-button" onClick={() => retryStep(3)}>
                  Retry
                </button>
              )}
            </div>
          </div>
          
          {/* Error Message Display */}
          {(steps.step1.status === 'error' || steps.step2.status === 'error' || steps.step3.status === 'error') && (
            <div className="progress-error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8v4M12 16h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>
                {steps.step1.status === 'error' ? steps.step1.error :
                 steps.step2.status === 'error' ? steps.step2.error : 
                 steps.step3.status === 'error' ? steps.step3.error : ''}
              </span>
            </div>
          )}
        </div>

        {/* Migration Result URL */}
        {(resultUrl || migrationResponse) && (
          <div className="migration-result">
            <div className="result-content">
              <h3>Migration Completed Successfully!</h3>
              {destination.toLowerCase() === 's3 bucket' && (migrationResponse?.data?.downloadUrl || migrationResponse?.data?.data?.downloadUrl) ? (
                <>
                  <p>Your migration has been completed. Click the button below to download the file:</p>
                  <a 
                    href={migrationResponse.data.downloadUrl || migrationResponse.data.data.downloadUrl} 
                    download
                    className="download-button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Download File
                  </a>
                </>
              ) : (
                <>
                  <p>Your migration has been completed. Click the link below to view the results:</p>
                  <a 
                    href={resultUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="result-link"
                  >
                    Open Migration Result
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </>
              )}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="modal-actions">
          <button 
            type="button" 
            className="cancel-button" 
            onClick={handleClose}
            disabled={isMigrationOngoing()}
            title={isMigrationOngoing() ? 'Cannot close during migration' : 'Close'}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestartMigrationModal;
